import { Flex, Skeleton, Stack, desktopFirstMediaQuery } from '@inflearn/ds-react';
import { isNil } from 'lodash-es';
import { type ReactNode, useEffect } from 'react';

import { useLoadableFeature } from '@hackler/react-sdk';
import { useDisclosure } from '@mantine/hooks';
import useCheckFollowing from '../../../hooks/useCheckFollowing';
import useCheckMyProfile from '../../../hooks/useCheckMyProfile';
import useQueryParams from '../../../hooks/useQueryParams';
import useUserProfileQuery from '../../../hooks/useUserProfileQuery';
import { NEW_LAYOUT } from '../../../lib/hackle/featureKeys';
import useClientAccountProfileQuery from '../../../queries/useClientAccountProfileQuery';
import { FollowingButton } from './FollowingButton';
import { HubProfile } from './HubProfile';
import { ReviewInformation } from './ReviewInformation';
import { NewUserNavigation, UserNavigation } from './UserNavigation';
import { UserProfile } from './UserProfile';
import { UserSlugModal } from './UserSlugModal';

export default function StudentProfileNavigation({
  children,
}: {
  children?: ReactNode;
}) {
  const { id } = useQueryParams();
  const { isMe, isLoading } = useCheckMyProfile();
  const { data: profileMeta } = useUserProfileQuery({ id: Number(id) });
  const { data: myProfile } = useClientAccountProfileQuery({ enabled: isMe });
  const [isSlugModalOpened, slugModalHandler] = useDisclosure(false);
  const { data: status } = useCheckFollowing(String(id));
  const { isOn } = useLoadableFeature(NEW_LAYOUT);

  useEffect(() => {
    if (myProfile && !myProfile.isSlugConfirmed) {
      slugModalHandler.open();
    }
  }, [myProfile]);

  if (isNil(profileMeta)) {
    return null;
  }

  return (
    <>
      <Flex
        mb={12}
        css={{
          gap: 20,
          flexDirection: 'column',
        }}
      >
        <Stack spacing={12}>
          <UserProfile
            slug={profileMeta.slug}
            name={profileMeta.name}
            thumbnailUrl={profileMeta.thumbnailUrl}
          />
          <HubProfile userId={profileMeta.id} userName={profileMeta.name} />
        </Stack>
        <ReviewInformation
          reviewCount={profileMeta.review.count}
          reviewAverage={profileMeta.review.average}
        />
        {isMe ? null : (
          <Skeleton
            visible={isLoading}
            css={{
              '&:empty': {
                display: 'none',
              },
              [desktopFirstMediaQuery.tablet]: {
                display: 'block',
              },
            }}
          >
            <FollowingButton
              userId={profileMeta.id}
              userName={profileMeta.name}
              isFollowing={Boolean(status?.isFollowing)}
            />
          </Skeleton>
        )}
      </Flex>
      {isOn ? (
        <NewUserNavigation
          userSlug={profileMeta.slug}
          hasBlog={profileMeta.hasBlogs}
          hasPost={profileMeta.hasPosts}
          hasRoadmap={profileMeta.hasRoadmaps}
          hasClip={profileMeta.hasClips}
        />
      ) : (
        <UserNavigation
          userSlug={profileMeta.slug}
          hasBlog={profileMeta.hasBlogs}
          hasPost={profileMeta.hasPosts}
          hasRoadmap={profileMeta.hasRoadmaps}
          hasClip={profileMeta.hasClips}
        />
      )}
      {children}
      {myProfile && (
        <UserSlugModal
          userId={myProfile.id}
          nickname={myProfile.name}
          thumbnailUrl={myProfile.thumbnail}
          initialSlug={myProfile.slug || ''}
          isOpened={isSlugModalOpened}
          onClose={slugModalHandler.close}
        />
      )}
    </>
  );
}
